import { setCsrfInvalidHandler, setCsrfToken, setUnauthorizedHandler } from "../api/client";
import type {
  AdminAccess,
  AdminCapability,
  OrganizationSummary,
  Permission,
  PortalFeature,
  RestrictedOrganization,
  User
} from "../api/contract";
import { getSession, logout as logoutEndpoint } from "../api/endpoints";
import { setTelemetryEnabled } from "../telemetry/emit";
import { prefs } from "./prefs.svelte";

class SessionStore {
  user = $state<User | null>(null);
  authMethod = $state<"email" | "password" | "saml" | "break_glass" | null>(null);
  admin = $state<AdminAccess>({ enabled: false, capabilities: [] });
  organizations = $state<OrganizationSummary[]>([]);
  /**
   * Organizations the account belongs to that the CURRENT NETWORK may not reach. They
   * are deliberately absent from `organizations` (no data for them is fetchable), so
   * the UI can only ever offer a denial + recovery notice for these.
   */
  restrictedOrganizations = $state<RestrictedOrganization[]>([]);
  /** True once the initial `/v1/session` probe has settled (success or 401). */
  ready = $state(false);

  get authenticated(): boolean {
    return this.user !== null;
  }

  get canManageEntitlements(): boolean {
    return this.admin.enabled && this.admin.capabilities.includes("entitlements.manage");
  }

  get canManageInvoices(): boolean {
    return this.admin.enabled && this.admin.capabilities.includes("invoices.manage");
  }

  /** Organization recovery operations. A strict superset gate — never inferred. */
  get canSupportOrganizations(): boolean {
    return this.hasCapability("organizations.support");
  }

  /**
   * Builders Discord operations. The server grants this only where the invitation
   * feature is enabled and wired, so its absence means "not in this environment"
   * rather than "this operator lacks access".
   */
  get canManageDiscord(): boolean {
    return this.hasCapability("discord.manage");
  }

  /** Generic support-capability probe, used by the router's `capability` guard. */
  hasCapability(capability: AdminCapability): boolean {
    return this.admin.enabled && this.admin.capabilities.includes(capability);
  }

  restrictedOrgById(orgId: string): RestrictedOrganization | undefined {
    return this.restrictedOrganizations.find((entry) => entry.id === orgId);
  }

  orgById(orgId: string): OrganizationSummary | undefined {
    return this.organizations.find((org) => org.id === orgId);
  }

  hasPermission(orgId: string, permission: Permission): boolean {
    return this.orgById(orgId)?.permissions.includes(permission) ?? false;
  }

  hasFeature(orgId: string, feature: PortalFeature): boolean {
    return this.orgById(orgId)?.features.includes(feature) ?? false;
  }

  /** Probe the session cookie at startup; populates user/orgs/CSRF or clears. */
  async bootstrap(): Promise<void> {
    try {
      const data = await getSession();
      this.#apply(data.user, data.authMethod, data.admin, data.organizations, data.restrictedOrganizations);
    } catch {
      this.clear();
    } finally {
      this.ready = true;
    }
  }

  /** Refresh user/orgs/CSRF after login or a mutation that changed org membership. */
  async refresh(): Promise<void> {
    const data = await getSession();
    this.#apply(data.user, data.authMethod, data.admin, data.organizations, data.restrictedOrganizations);
  }

  /** Silent CSRF refresh after a `csrf_invalid` (G-5). The user still resubmits. */
  async refreshCsrf(): Promise<void> {
    try {
      await getSession();
    } catch {
      /* leave state as-is; the failing mutation surfaces its own error */
    }
  }

  async logout(): Promise<void> {
    try {
      await logoutEndpoint();
    } catch {
      /* even if the revoke call fails we drop local state */
    }
    this.clear();
  }

  clear(): void {
    this.user = null;
    this.authMethod = null;
    this.admin = { enabled: false, capabilities: [] };
    this.organizations = [];
    this.restrictedOrganizations = [];
    setCsrfToken(null);
    setTelemetryEnabled(false);
  }

  #apply(
    user: User,
    authMethod: "email" | "password" | "saml" | "break_glass",
    admin: AdminAccess,
    organizations: OrganizationSummary[],
    restrictedOrganizations: RestrictedOrganization[]
  ): void {
    this.user = user;
    this.authMethod = authMethod;
    this.admin = admin;
    this.organizations = organizations;
    this.restrictedOrganizations = restrictedOrganizations;
    setTelemetryEnabled(true);
    // Keep the persisted org pointer valid.
    if (prefs.lastOrgId && !this.orgById(prefs.lastOrgId)) {
      prefs.setLastOrg(organizations[0]?.id ?? null);
    } else if (!prefs.lastOrgId && organizations.length) {
      prefs.setLastOrg(organizations[0]!.id);
    }
  }
}

export const session = new SessionStore();

/** Wire the client's auth callbacks. Called once from main.ts with a redirect hook. */
export function wireAuthHandlers(onSignedOut: () => void): void {
  setCsrfInvalidHandler(() => {
    void session.refreshCsrf();
  });
  setUnauthorizedHandler(() => {
    if (session.authenticated) {
      session.clear();
      onSignedOut();
    }
  });
}
